import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { genSectionCode } from '../services/client.service';
import { getCountries } from './geo';
import { getCountryCode, isValidEmail } from './utils';
import {
    getTemplateLogoBuffer, addTemplateBrandBanner, styleTemplateHeaderRow,
    parseClassInfoBlockFromAoa, parseLicenseRowsFromAoa,
    CLASS_INFO_COLUMN_NAMES, CLASS_INFO_EXAMPLE_ROW, STUDENT_COLUMN_NAMES, STUDENT_EXAMPLE_VALUES, CLASS_SHEET_NAME,
} from './analyticsLicensesExcel';
import {
    TEMPLATE_PARTNER, TEMPLATE_NETWORK_SCHOOL, TEMPLATE_NETWORK_CLASS, TEMPLATE_SCHOOL_CLASS,
    detectImportTemplate, readImportTemplateRows, fillImportTemplate, loadTemplateLookup,
    countryToIso, countryNameFor, countryCodeTextFor, dialFromCountryCode, blankIfNA, NOT_APPLICABLE,
    onTemplateList, regionBelongsToCountry, stateForExport, stateToCode,
} from './importTemplates';

/**
 * Schools / School Networks upload — the approved templates of 4 Oct 2026:
 *
 *   SNW_template.xlsx  (a School Network adds its schools, and their classes)
 *       sheet "School"   one school per row
 *       sheet "Class 1"  one class + its students; "School Email" says which school
 *   PW_template.xlsx   (a Partner adds networks and schools)
 *       sheet "School"   the network row(s) on top, then one school per row;
 *                        "Network Email (…)" puts a school in a network
 *
 * This file only builds the templates and READS an uploaded file into plain
 * rows with their problems listed. It calls no API: the dialog decides what to
 * send (BulkUploadSchoolsDialog), through the same create calls the Add a
 * School / Add a Network forms use.
 *
 * Columns are found by their header text, never by position, so a column that
 * is moved or a sheet that is renamed still reads correctly.
 */

export const UPLOAD_KIND_NETWORK = 'network'; // a School Network workspace — SNW template
export const UPLOAD_KIND_PARTNER = 'partner'; // a Partner workspace — PW template

const HIGHLIGHT_FILL = 'FFFFC000'; // the amber the approved templates give their linking column
const EXAMPLE_FILL = 'FFF3F6F8';
const EXAMPLE_TEXT = 'FF4B5563';
const THIN = { style: 'thin', color: { argb: 'FFE5E7EB' } };
const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

const SCHOOL_SHEET_NAME = 'School';
const TITLE_NETWORKS = 'DQ — School Networks Upload Template';
const TITLE_SCHOOLS = 'DQ — Schools Upload Template';
const TITLE_CLASS = 'DQ — Class Upload Template';
const TITLE_STUDENTS = 'DQ — Students Upload Template';
const SCHOOL_EMAIL_LABEL = 'School Email';
// Was "Network Email (optional)" — read as "not needed", when it is what joins a
// school to its network (Lydia / Jamil, 6 Oct 2026). Files with the old header
// still upload: the column is matched on "Network Email" (see normHeader).
const NETWORK_EMAIL_LABEL = 'Network Email (Email of the School Network account to connect to)';
/** The column's short name, for the problem list. */
const NETWORK_EMAIL_NAME = 'Network Email';

/**
 * The fourteen columns both blocks share, in the approved order. `who` is
 * "School" or "Network" — the only thing that differs between the two blocks.
 * `required` follows the Add a School / Add a Network form exactly.
 */
// `max` is the length limit "Schema for Data Collection" gives the field.
const accountColumns = (who) => [
    { key: 'org_name', label: `${who} Name`, required: true, max: 200 },
    { key: 'contact_first_name', label: 'First Name', required: true, max: 100 },
    { key: 'contact_last_name', label: 'Last Name', required: true, max: 100 },
    { key: 'contact_title', label: 'Title / Role (optional)', max: 100 },
    { key: 'contact_email', label: 'Contact Email', required: true, email: true, max: 254 },
    { key: 'dial_code', label: 'Dial Code' },
    { key: 'phone', label: `${who} Phone Number` },
    { key: 'website', label: `${who} Website (optional)`, max: 300 },
    { key: 'address1', label: `${who} Address - Line 1`, required: true, max: 200 },
    { key: 'address2', label: `${who} Address - Line 2 (optional)`, max: 200 },
    { key: 'country', label: 'Country', required: true },
    { key: 'state', label: `${who} State / Province / Region`, max: 100 },
    { key: 'city', label: 'City', required: true, max: 100 },
    { key: 'postal_code', label: 'Postal Code', required: true, max: 16 },
];

export const SCHOOL_COLUMNS = accountColumns('School');
export const NETWORK_COLUMNS = accountColumns('Network');
const NETWORK_EMAIL_COLUMN = { key: 'network_email', label: NETWORK_EMAIL_LABEL, name: NETWORK_EMAIL_NAME, email: true };

/* ───────────────────────────── templates ───────────────────────────── */

const setWidths = (ws, labels) => {
    labels.forEach((label, i) => {
        const col = ws.getColumn(i + 1);
        col.width = Math.max(col.width || 0, Math.min(Math.max(String(label).length + 4, 14), 42));
    });
};

/** A merged title band on its own row — the "DQ — … Upload Template" lines. */
const addTitleRow = (ws, rowNum, title, cols) => {
    ws.getCell(rowNum, 1).value = title;
    ws.mergeCells(rowNum, 1, rowNum, cols);
    const cell = ws.getCell(rowNum, 1);
    cell.font = { bold: true, size: 13, color: { argb: 'FF052F44' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXAMPLE_FILL } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    ws.getRow(rowNum).height = 26;
    /*
     * A title that sits BETWEEN the rows of data gets typed over — it looks like
     * the next free row (Lydia, 6 Oct 2026: a third network's name on the schools
     * title). The cell therefore refuses new text and says where the row belongs.
     * The reader skips a typed-over title anyway (mergedBandRows); this stops the
     * mistake where it is made.
     */
    cell.dataValidation = {
        type: 'custom',
        formulae: ['FALSE'],
        allowBlank: true,
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Section title',
        error: 'This row is a section title. To add another row, insert a new row above it and fill in its columns.',
    };
};

const addHeaderRow = (ws, rowNum, labels, highlightLabel) => {
    labels.forEach((label, i) => { ws.getCell(rowNum, i + 1).value = label; });
    styleTemplateHeaderRow(ws, rowNum);
    if (highlightLabel) {
        const idx = labels.indexOf(highlightLabel);
        if (idx >= 0) {
            const cell = ws.getCell(rowNum, idx + 1);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HIGHLIGHT_FILL } };
            cell.font = { bold: true, size: 11, color: { argb: 'FF111827' } };
        }
    }
};

/** A row to type into (or an example), in the muted placeholder style. */
const addInputRow = (ws, rowNum, values, cols) => {
    for (let c = 1; c <= cols; c += 1) {
        const cell = ws.getCell(rowNum, c);
        if (values && values[c - 1] !== undefined && values[c - 1] !== '') cell.value = values[c - 1];
        cell.font = { italic: true, color: { argb: EXAMPLE_TEXT }, size: 10.5 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXAMPLE_FILL } };
        cell.border = BORDER;
    }
    ws.getRow(rowNum).height = 20;
};

/** SNW "School" sheet — title row 2, headers row 4, first school on row 5. */
const addNetworkSchoolSheet = (wb, logoId) => {
    const ws = wb.addWorksheet(SCHOOL_SHEET_NAME);
    const labels = SCHOOL_COLUMNS.map((c) => c.label);
    addTemplateBrandBanner(wb, ws, TITLE_SCHOOLS, labels.length, logoId); // rows 1-3
    addHeaderRow(ws, 4, labels);
    addInputRow(ws, 5, null, labels.length);
    ws.views = [{ state: 'frozen', ySplit: 4 }];
    setWidths(ws, labels);
};

/**
 * SNW "Class 1" sheet — class block on rows 3-4 (with "School Email"), the
 * student table from row 10. Row numbers are the approved file's own.
 */
const addNetworkClassSheet = (wb, logoId) => {
    const ws = wb.addWorksheet(CLASS_SHEET_NAME);
    const classLabels = [...CLASS_INFO_COLUMN_NAMES, SCHOOL_EMAIL_LABEL];
    const cols = STUDENT_COLUMN_NAMES.length;
    // Row 1 logo + row 2 title; the class headers sit directly under the title.
    addTemplateBrandBanner(wb, ws, TITLE_CLASS, cols, logoId);
    ws.getRow(3).height = 24;
    for (let c = 1; c <= cols; c += 1) ws.getCell(3, c).fill = { type: 'pattern', pattern: 'none' };
    addHeaderRow(ws, 3, classLabels, SCHOOL_EMAIL_LABEL);
    addInputRow(ws, 4, CLASS_INFO_EXAMPLE_ROW, classLabels.length);
    addTitleRow(ws, 9, TITLE_STUDENTS, cols);
    addHeaderRow(ws, 10, STUDENT_COLUMN_NAMES);
    addInputRow(ws, 11, STUDENT_EXAMPLE_VALUES, cols);
    ws.views = [{ state: 'frozen', ySplit: 10 }];
    setWidths(ws, STUDENT_COLUMN_NAMES);
    setWidths(ws, classLabels);
};

/** PW "School" sheet — network block rows 3-5, schools table from row 7. */
const addPartnerSheet = (wb, logoId) => {
    const ws = wb.addWorksheet(SCHOOL_SHEET_NAME);
    const netLabels = NETWORK_COLUMNS.map((c) => c.label);
    const schoolLabels = [...SCHOOL_COLUMNS.map((c) => c.label), NETWORK_EMAIL_LABEL];
    addTemplateBrandBanner(wb, ws, TITLE_NETWORKS, netLabels.length, logoId);
    ws.getRow(3).height = 24;
    for (let c = 1; c <= netLabels.length; c += 1) ws.getCell(3, c).fill = { type: 'pattern', pattern: 'none' };
    addHeaderRow(ws, 3, netLabels);
    addInputRow(ws, 4, null, netLabels.length);
    addInputRow(ws, 5, null, netLabels.length);
    addTitleRow(ws, 6, TITLE_SCHOOLS, netLabels.length);
    addHeaderRow(ws, 7, schoolLabels, NETWORK_EMAIL_LABEL);
    addInputRow(ws, 8, null, schoolLabels.length);
    ws.views = [{ state: 'frozen', ySplit: 7 }];
    setWidths(ws, schoolLabels);
    setWidths(ws, netLabels);
    // The Network Email header says what it is for — wide enough to read on one line.
    ws.getColumn(schoolLabels.length).width = NETWORK_EMAIL_LABEL.length + 4;
};

/** The template for this workspace: SNW for a network, PW for a partner. */
export const buildSchoolsUploadTemplateWorkbook = async (kind) => {
    const wb = new ExcelJS.Workbook();
    const logoBuf = await getTemplateLogoBuffer();
    const logoId = logoBuf ? wb.addImage({ buffer: logoBuf, extension: 'png' }) : null;
    if (kind === UPLOAD_KIND_PARTNER) {
        addPartnerSheet(wb, logoId);
    } else {
        addNetworkSchoolSheet(wb, logoId);
        addNetworkClassSheet(wb, logoId);
    }
    return wb;
};

/* ── Export … Details: the same template, filled with what is on file ─────────
 *
 * Same sheet, banner, header rows and columns as the template above, so the file
 * a workspace exports looks exactly like the one it uploads (8 Oct 2026). Only
 * the school sheet: a network's "Class 1" sheet is exported from My Classes.
 */

/**
 * Switchable priority flag:
 * - true (default): For schools, export primarily from institute_* with org_* fallback.
 * - false: Serve directly from org_* for all accounts.
 */
const PREFER_INSTITUTE_FOR_SCHOOLS = true;

/** One account from the client list, as the template's column values (by key). */
const accountExportValues = (c = {}) => {
    const parts = String(c.contact_name || '').trim().split(/\s+/).filter(Boolean);
    const isNetwork = Boolean(c.is_school_network);

    // For a school (standalone or sub-school), resolve with institute_* priority and org_* fallback.
    // For a network, export directly from org_*.
    const pick = (instVal, orgVal) => {
        if (isNetwork || !PREFER_INSTITUTE_FOR_SCHOOLS) {
            return (orgVal !== undefined && orgVal !== null && orgVal !== '') ? orgVal : (instVal ?? '');
        }
        return (instVal !== undefined && instVal !== null && instVal !== '') ? instVal : (orgVal ?? '');
    };

    const rawCountry = pick(c.institute_head_country, c.country || c.org_country);
    const code = String(rawCountry || '').toUpperCase();
    const countryLabel = (getCountries().find((x) => x.code === code) || {}).label || rawCountry || '';

    return {
        org_name: c.org_name || '',
        contact_first_name: c.contact_first_name || (c.contact_last_name ? '' : parts[0]) || '',
        contact_last_name: c.contact_last_name || (c.contact_first_name ? '' : parts.slice(1).join(' ')) || '',
        contact_title: pick(c.institute_head_title, c.contact_title),
        contact_email: c.contact_email || '',
        dial_code: pick(c.institute_phone_country_code, c.org_phone_country_code),
        phone: pick(c.institute_phone_number, c.org_phone_number),
        website: pick(c.institute_website, c.org_website),
        address1: pick(c.institute_address_line1 || c.institute_address, c.org_address),
        address2: pick(c.institute_address_line2, c.org_address_line2),
        country: countryLabel,
        state: pick(c.institute_state, c.org_state),
        city: pick(c.institute_head_city, c.org_city),
        postal_code: pick(c.institute_head_postal_code, c.postal_code),
    };
};

/** A filled row — plain text, bordered; not the template's muted placeholder style. */
const addDataRow = (ws, rowNum, values) => {
    values.forEach((v, i) => {
        const cell = ws.getCell(rowNum, i + 1);
        cell.value = v === undefined || v === null ? '' : String(v);
        cell.font = { size: 10.5, color: { argb: 'FF111827' } };
        cell.border = BORDER;
    });
    ws.getRow(rowNum).height = 20;
};

const exportTitle = (templateTitle) => templateTitle.replace(' Upload Template', '');

/**
 * @param {string}   kind     UPLOAD_KIND_NETWORK | UPLOAD_KIND_PARTNER
 * @param {object[]} clients  every row of the My Schools list (a partner's network
 *                            rows carry their own schools in `schools`)
 */
export const buildSchoolsExportWorkbook = async (kind, clients = []) => {
    const wb = new ExcelJS.Workbook();
    const logoBuf = await getTemplateLogoBuffer();
    const logoId = logoBuf ? wb.addImage({ buffer: logoBuf, extension: 'png' }) : null;
    const ws = wb.addWorksheet(SCHOOL_SHEET_NAME);
    const rowOf = (columns, c) => { const v = accountExportValues(c); return columns.map((col) => v[col.key] ?? ''); };

    if (kind !== UPLOAD_KIND_PARTNER) {
        // SNW "School" sheet — title row 2, headers row 4, schools from row 5.
        const labels = SCHOOL_COLUMNS.map((c) => c.label);
        addTemplateBrandBanner(wb, ws, exportTitle(TITLE_SCHOOLS), labels.length, logoId);
        addHeaderRow(ws, 4, labels);
        clients.forEach((c, i) => addDataRow(ws, 5 + i, rowOf(SCHOOL_COLUMNS, c)));
        ws.views = [{ state: 'frozen', ySplit: 4 }];
        setWidths(ws, labels);
        return wb;
    }

    // PW "School" sheet — the networks block, then the schools table.
    const networks = clients.filter((c) => c.is_school_network);
    const schools = [
        ...clients.filter((c) => !c.is_school_network).map((c) => ({ c, network_email: '' })),
        ...networks.flatMap((n) => (n.schools || []).map((c) => ({ c, network_email: n.contact_email || '' }))),
    ];
    const netLabels = NETWORK_COLUMNS.map((c) => c.label);
    const schoolLabels = [...SCHOOL_COLUMNS.map((c) => c.label), NETWORK_EMAIL_LABEL];
    addTemplateBrandBanner(wb, ws, exportTitle(TITLE_NETWORKS), netLabels.length, logoId);
    ws.getRow(3).height = 24;
    for (let c = 1; c <= netLabels.length; c += 1) ws.getCell(3, c).fill = { type: 'pattern', pattern: 'none' };
    addHeaderRow(ws, 3, netLabels);
    networks.forEach((n, i) => addDataRow(ws, 4 + i, rowOf(NETWORK_COLUMNS, n)));
    // The template leaves two rows for networks; the schools table follows them.
    const titleRow = 4 + Math.max(networks.length, 2);
    addTitleRow(ws, titleRow, exportTitle(TITLE_SCHOOLS), netLabels.length);
    addHeaderRow(ws, titleRow + 1, schoolLabels, NETWORK_EMAIL_LABEL);
    schools.forEach(({ c, network_email }, i) => addDataRow(ws, titleRow + 2 + i, [...rowOf(SCHOOL_COLUMNS, c), network_email]));
    ws.views = [{ state: 'frozen', ySplit: 3 }];
    setWidths(ws, schoolLabels);
    setWidths(ws, netLabels);
    ws.getColumn(schoolLabels.length).width = NETWORK_EMAIL_LABEL.length + 4;
    return wb;
};

/** The import template for this workspace's My Schools (Ian, 13 Oct 2026). */
export const schoolsImportTemplateKind = (kind) => (kind === UPLOAD_KIND_PARTNER ? TEMPLATE_PARTNER : TEMPLATE_NETWORK_SCHOOL);

/**
 * Export … Details in the import template itself — one file for importing and
 * exporting (Ian, 13 Oct 2026). A partner's file lists each network, then the
 * schools inside it, then the standalone schools; a network's lists its schools.
 * @returns {Promise<Blob>}
 */
export const buildSchoolsExportBlob = async (kind, clients = []) => {
    const templateKind = schoolsImportTemplateKind(kind);
    const lookup = await loadTemplateLookup(templateKind);
    const rowOf = (c, extra = {}) => {
        const v = accountExportValues(c);
        return {
            ...v,
            country_code: countryCodeTextFor(lookup, v.dial_code, v.country),
            country: countryNameFor(lookup, v.country),
            state: stateForExport(lookup, v.country, v.state),
            ...extra,
        };
    };
    if (kind !== UPLOAD_KIND_PARTNER) {
        return fillImportTemplate(templateKind, clients.map((c) => rowOf(c)));
    }
    const rows = [];
    clients.filter((c) => c.is_school_network).forEach((n) => {
        // Parent Network Email is left to the template's own formula: N/A for a network.
        rows.push(rowOf(n, { type: 'Network' }));
        (n.schools || []).forEach((c) => rows.push(rowOf(c, { type: 'School', parent_network_email: n.contact_email || NOT_APPLICABLE })));
    });
    clients.filter((c) => !c.is_school_network)
        .forEach((c) => rows.push(rowOf(c, { type: 'School', parent_network_email: NOT_APPLICABLE })));
    return fillImportTemplate(templateKind, rows);
};

/** Named after the button that produces it: Export School Details / Export Network/School Details. */
export const schoolsExportFileName = (kind) => (kind === UPLOAD_KIND_PARTNER ? 'Network and School Details' : 'School Details');

// Full names, not the PW / SNW abbreviations (8 Oct 2026).
export const schoolsTemplateFileName = (kind) => (kind === UPLOAD_KIND_PARTNER ? 'Partner Workspace Template' : 'School Network Workspace Template');

/* ────────────────────────────── reading ────────────────────────────── */

const text = (v) => (v == null || typeof v === 'object' ? '' : String(v).trim());

/** Header text as compared: lower case, "(optional)" dropped, spaces collapsed. */
const normHeader = (v) => text(v).toLowerCase().replace(/\(optional\)/g, '').replace(/\s+/g, ' ')
    // the approved PW file spells this one "Regoin"
    .replace('regoin', 'region')
    // "Network Email (optional)" and "Network Email (Email of the …)" are one column
    .replace(/^network email.*$/, 'network email')
    .trim();

const rowIsEmpty = (row) => !(row || []).some((c) => text(c) !== '');
// A title band ("DQ — Schools Upload Template"). Matched on the words "Upload
// Template" alone — not on the "DQ" or the dash — so a title that was retyped
// ("School Upload Template", a hyphen for the dash, a leading space) is still a
// title and never read as a row of data.
const isTitleRow = (row) => /upload\s+template/i.test(text((row || []).find((c) => text(c) !== '')));

/** Index of the first row that has a cell reading `label`, or -1. */
const findHeaderRow = (aoa, label, from = 0) => {
    const want = normHeader(label);
    for (let i = from; i < aoa.length; i += 1) {
        if ((aoa[i] || []).some((c) => normHeader(c) === want)) return i;
    }
    return -1;
};

/**
 * The rows that are ONE merged band across the table — the template's section
 * titles ("DQ — Schools Upload Template"). A band is a title whatever it says:
 * its cells are merged into one, so it can never hold a row of data. Typing a
 * network's name over the title (Lydia, 6 Oct 2026) therefore does not turn it
 * into a network with every other field missing.
 * Returned as indexes into the sheet's rows as read (`aoa`).
 */
const mergedBandRows = (sheet) => {
    const first = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0;
    const rows = new Set();
    (sheet['!merges'] || []).forEach((m) => {
        if (m.s.r === m.e.r && m.s.c === 0 && m.e.c - m.s.c >= 3) rows.add(m.s.r - first);
    });
    return rows;
};

/**
 * Rows of one block, read by header. Stops at the block's end: a title band,
 * another block's header (`stopLabels`), or the end of the sheet.
 */
const readBlock = (aoa, headerIdx, columns, stopLabels = [], bands = new Set()) => {
    const header = (aoa[headerIdx] || []).map(normHeader);
    const idxOf = (label) => header.indexOf(normHeader(label));
    const map = columns.map((c) => ({ ...c, idx: idxOf(c.label) }));
    const stops = stopLabels.map(normHeader);
    const rows = [];
    for (let i = headerIdx + 1; i < aoa.length; i += 1) {
        const row = aoa[i] || [];
        if (rowIsEmpty(row)) continue;
        if (bands.has(i) || isTitleRow(row) || row.some((c) => stops.includes(normHeader(c)))) break;
        const values = { _row: i + 1 };
        map.forEach((c) => { values[c.key] = c.idx >= 0 ? text(row[c.idx]) : ''; });
        rows.push(values);
    }
    return { rows, missing: map.filter((c) => c.idx < 0 && c.required).map((c) => c.label) };
};

/** "65", "+65", "0065" → "+65"; anything else is returned as typed, flagged by the checks. */
const normDialCode = (v) => {
    const s = text(v).replace(/\s+/g, '').replace(/^00/, '+');
    if (!s) return '';
    return /^\+?\d{1,4}$/.test(s) ? `+${s.replace('+', '')}` : s;
};

/** A country as typed — its name or its two-letter code — to the code the API stores, or ''. */
const resolveCountry = (v) => {
    const raw = text(v);
    if (!raw) return '';
    const code = String(getCountryCode(raw) || '').toUpperCase();
    return getCountries().some((c) => c.code === code) ? code : '';
};

const lower = (v) => text(v).toLowerCase();

/** The problems of one account row, as { sheet, row, column, message } entries. */
const checkAccountRow = (sheet, values, columns, who) => {
    const out = [];
    const add = (column, message) => out.push({ sheet, row: values._row, column, message });
    columns.forEach((c) => {
        const v = values[c.key];
        if (c.required && !v) add(c.label, 'Required');
        else if (c.email && v && !isValidEmail(v)) add(c.name || c.label, 'Invalid email address');
        else if (c.max && v.length > c.max) add(c.label, `Too long — ${c.max} characters at most`);
    });
    if (values.country && !resolveCountry(values.country)) {
        add('Country', `"${values.country}" is not a country we recognise — use the country name or its two-letter code`);
    }
    if (values.dial_code && !/^\+\d{1,4}$/.test(normDialCode(values.dial_code))) {
        add('Dial Code', `"${values.dial_code}" is not a dial code — for example +65`);
    }
    // Schema for Data Collection: "Digits, space, + and - only. 6-20 chars."
    if (values.phone && !/^[\d\s+-]{6,20}$/.test(values.phone)) {
        add(`${who} Phone Number`, 'Use digits, spaces, + and - only, 6 to 20 characters, without the dial code');
    }
    if (values.website && !/^(https?:\/\/)?[^\s.]+\.[^\s]+$/i.test(values.website)) {
        add(`${who} Website (optional)`, `"${values.website}" is not a web address`);
    }
    return out;
};

/* ── the import templates of 13 Oct 2026: one flat table, data from row 5 ── */

const IMPORT_ACCOUNT_RULES = [
    { key: 'org_name', required: true, max: 200 },
    { key: 'contact_first_name', label: 'Contact First Name', required: true, max: 100 },
    { key: 'contact_last_name', label: 'Contact Last Name', required: true, max: 100 },
    { key: 'contact_title', label: 'Contact Title / Role', max: 100 },
    { key: 'contact_email', label: 'Contact Email', required: true, email: true, max: 254 },
    { key: 'country_code', label: 'Country Code', required: true },
    { key: 'phone', label: 'Phone Number', required: true },
    { key: 'website', label: 'Website', max: 300 },
    { key: 'address1', label: 'Address - Line 1', required: true, max: 200 },
    { key: 'address2', label: 'Address - Line 2', max: 200 },
    { key: 'country_typed', label: 'Country', required: true },
    { key: 'state_typed', label: 'State / Province / Region', required: true, max: 100 },
    { key: 'city', label: 'City', required: true, max: 100 },
    { key: 'postal_code', label: 'Postal Code', required: true, max: 16 },
];

/** The problems of one row of an import template, by the template's own column names. */
const checkImportAccountRow = (sheet, r, nameLabel, lookup) => {
    const out = [];
    const add = (column, message) => out.push({ sheet, row: r._row, column, message });
    IMPORT_ACCOUNT_RULES.forEach((c) => {
        const label = c.label || nameLabel;
        const v = r[c.key] || '';
        if (c.required && !v) add(label, c.key === 'state_typed' ? `Required — select ${NOT_APPLICABLE} if the country has no regions` : 'Required');
        else if (c.email && v && !isValidEmail(v)) add(label, 'Invalid email address');
        else if (c.max && v.length > c.max) add(label, `Too long — ${c.max} characters at most`);
    });
    if (r.country_typed && !r.country) add('Country', `"${r.country_typed}" is not on the Country list — select it from the dropdown`);
    else if (r.country && r.state_typed && !regionBelongsToCountry(lookup, r.country_typed, r.state_typed)) add('State / Province / Region', `"${r.state_typed}" is not a region of ${r.country_typed} — select Country first, then a region from the list`);
    if (r.country_code && (!/^\+\d{1,4}$/.test(r.dial_code) || !onTemplateList(lookup, 'Dial code', r.country_code))) add('Country Code', `"${r.country_code}" is not on the Country Code list — for example Singapore (+65)`);
    // The template's own cell rule: digits only, without the country code. The
    // server also needs 6 to 20 of them, so a shorter number is caught here
    // instead of failing on Import.
    if (r.phone && !/^\d{6,20}$/.test(r.phone)) add('Phone Number', 'Digits only (6 to 20), without the country code — no spaces, hyphens, brackets or +');
    if (r.website && !/^(https?:\/\/)?[^\s.]+\.[^\s]+$/i.test(r.website)) add('Website', `"${r.website}" is not a web address`);
    return out;
};

/**
 * The rows of an import template, in the shape the rest of this file works
 * with (the keys the older templates were read into), so creating the accounts
 * is unchanged.
 */
const readImportTemplateAccounts = (wb, found, isPartner, existingEmails, out) => {
    const { rows, missing, lookup } = readImportTemplateRows(wb, found);
    const sheet = found.sheetName;
    if (missing.length) out.errors.push({ sheet, row: found.headerRow + 1, column: missing.join(', '), message: 'Column missing — keep the template\'s columns as they are' });
    const partnerFile = found.kind === TEMPLATE_PARTNER;
    rows.forEach((raw) => {
        const type = partnerFile ? lower(raw.type) : 'school';
        const r = {
            _row: raw._row,
            _sheet: sheet,
            org_name: raw.org_name,
            contact_first_name: raw.contact_first_name,
            contact_last_name: raw.contact_last_name,
            contact_title: raw.contact_title,
            contact_email: raw.contact_email,
            country_code: raw.country_code,
            dial_code: dialFromCountryCode(raw.country_code),
            phone: raw.phone,
            website: raw.website,
            address1: raw.address1,
            address2: raw.address2,
            // The code the API stores; what was typed is kept for the message.
            country_typed: raw.country,
            country: countryToIso(lookup, raw.country),
            // N/A is the dropdown's "this country has no regions".
            state_typed: raw.state,
            state: stateToCode(countryToIso(lookup, raw.country), raw.state),
            city: raw.city,
            postal_code: raw.postal_code,
            network_email: partnerFile ? blankIfNA(raw.parent_network_email) : '',
        };
        r._exists = existingEmails.has(lower(r.contact_email));
        if (partnerFile && type !== 'school' && type !== 'network') {
            out.errors.push({ sheet, row: r._row, column: 'Type', message: raw.type ? `"${raw.type}" is not a type — select School or Network` : 'Required — select School or Network' });
            return;
        }
        if (type === 'network' && !isPartner) {
            out.notes.push(`Row ${r._row}: the network "${r.org_name || r.contact_email}" was ignored — a network adds schools, not other networks.`);
            return;
        }
        // A row already in the workspace is refused for its email (below); its other gaps are not repeated.
        if (!r._exists) out.errors.push(...checkImportAccountRow(sheet, r, partnerFile ? 'School or Network Name' : 'School Name', lookup));
        // Required for a School row (the Guide): N/A, or its network's Contact Email.
        if (partnerFile && type === 'school' && !r._exists && !String(raw.parent_network_email || '').trim()) {
            out.errors.push({ sheet, row: r._row, column: 'Parent Network Email', message: `Required — select ${NOT_APPLICABLE} for a school with no network, or its network's Contact Email` });
        }
        // The template's own rule: a Network row's Parent Network Email is N/A.
        if (type === 'network' && blankIfNA(raw.parent_network_email)) {
            out.errors.push({ sheet, row: r._row, column: 'Parent Network Email', message: `A network has no parent network — leave it ${NOT_APPLICABLE}` });
        }
        if (type === 'network') out.networks.push({ ...r, network_email: '' });
        else out.schools.push(isPartner ? r : { ...r, network_email: '' });
    });
};

/**
 * Read an uploaded file.
 *
 * @param {File} file
 * @param {'network'|'partner'} kind
 * @param {{ emails?: string[], networks?: {email: string, org_code: string, org_name: string}[],
 *           schools?: {email: string, org_code: string, institute_code: string, org_name: string}[] }} [existing]
 *        what the workspace already has, so a row that exists is skipped (a
 *        file can be uploaded again) and a class or school can point at a
 *        school or network created earlier.
 * @returns {Promise<{ networks: object[], schools: object[], classes: object[],
 *                     errors: object[], notes: string[], fatal?: string }>}
 */
export const parseSchoolsUploadWorkbook = async (file, kind, existing = {}) => {
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const out = { networks: [], schools: [], classes: [], errors: [], notes: [] };
    if (!wb.SheetNames.length) return { ...out, fatal: 'The file has no sheets.' };

    const isPartner = kind === UPLOAD_KIND_PARTNER;
    const existingEmails = new Set((existing.emails || []).map(lower).filter(Boolean));
    const existingNetworks = new Map((existing.networks || []).map((n) => [lower(n.email), n]));
    const existingSchools = new Map((existing.schools || []).map((s) => [lower(s.email), s]));
    let foundSchoolBlock = false;

    /*
     * The import templates of 13 Oct 2026. Any other file — an older template,
     * an export made before — is still read by the block reader below.
     */
    const importTemplate = detectImportTemplate(wb);
    if (importTemplate && [TEMPLATE_NETWORK_CLASS, TEMPLATE_SCHOOL_CLASS].includes(importTemplate.kind)) {
        return { ...out, fatal: 'This is the classes and learners template — upload it from My Classes. My Schools takes the schools template.' };
    }
    if (importTemplate) {
        foundSchoolBlock = true;
        readImportTemplateAccounts(wb, importTemplate, isPartner, existingEmails, out);
    }

    for (const sheetName of (importTemplate ? [] : wb.SheetNames)) {
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
        const bands = mergedBandRows(wb.Sheets[sheetName]);
        const schoolHeader = findHeaderRow(aoa, 'School Name');
        const networkHeader = findHeaderRow(aoa, 'Network Name');
        const classHeader = findHeaderRow(aoa, 'Class Name');
        // Something typed over a section title BETWEEN the rows of data is not read —
        // say so, and where it belongs. (The title at the very top holds no data either way.)
        const firstHeader = Math.min(...[schoolHeader, networkHeader, classHeader].filter((h) => h >= 0), Infinity);
        bands.forEach((i) => {
            if (i < firstHeader) return;
            const typed = text((aoa[i] || []).find((c) => text(c) !== ''));
            if (typed && !isTitleRow(aoa[i])) {
                out.notes.push(`Sheet "${sheetName}", row ${i + 1} is a section title row (merged cells), so "${typed}" was not read. To add another row, insert a new row above it and fill in all of its columns.`);
            }
        });

        /* ── networks (partner file only) ── */
        if (networkHeader >= 0) {
            if (!isPartner) {
                out.notes.push(`Sheet "${sheetName}": the network rows were ignored — a network adds schools, not other networks.`);
            } else {
                const { rows, missing } = readBlock(aoa, networkHeader, NETWORK_COLUMNS, ['School Name'], bands);
                if (missing.length) out.errors.push({ sheet: sheetName, row: networkHeader + 1, column: missing.join(', '), message: 'Column missing from the network block' });
                rows.forEach((r) => {
                    out.errors.push(...checkAccountRow(sheetName, r, NETWORK_COLUMNS, 'Network'));
                    out.networks.push({ ...r, _sheet: sheetName, _exists: existingEmails.has(lower(r.contact_email)) });
                });
            }
        }

        /* ── schools ── */
        if (schoolHeader >= 0) {
            foundSchoolBlock = true;
            const columns = isPartner ? [...SCHOOL_COLUMNS, NETWORK_EMAIL_COLUMN] : SCHOOL_COLUMNS;
            const { rows, missing } = readBlock(aoa, schoolHeader, columns, ['Network Name'], bands);
            if (missing.length) out.errors.push({ sheet: sheetName, row: schoolHeader + 1, column: missing.join(', '), message: 'Column missing from the schools table' });
            rows.forEach((r) => {
                out.errors.push(...checkAccountRow(sheetName, r, columns, 'School'));
                out.schools.push({ ...r, _sheet: sheetName, _exists: existingEmails.has(lower(r.contact_email)) });
            });
        }

        /* ── a class sheet: class block + student table ── */
        if (classHeader >= 0 && schoolHeader < 0 && networkHeader < 0) {
            if (isPartner) {
                out.notes.push(`Sheet "${sheetName}": classes were ignored — classes and students are uploaded from the network or the school workspace.`);
                continue;
            }
            const headerCells = (aoa[classHeader] || []).map(normHeader);
            const emailIdx = headerCells.indexOf(normHeader(SCHOOL_EMAIL_LABEL));
            const schoolEmail = emailIdx >= 0 ? text((aoa[classHeader + 1] || [])[emailIdx]) : '';
            const info = parseClassInfoBlockFromAoa(aoa);
            if (!schoolEmail) {
                // The template's own example class, left as it came: not a class
                // anyone asked for, so it is left out.
                const untouched = !info || (info.class_name === CLASS_INFO_EXAMPLE_ROW[0]
                    && info.incharge_email === CLASS_INFO_EXAMPLE_ROW[5]);
                if (untouched) {
                    out.notes.push(`Sheet "${sheetName}": the example class was left out.`);
                } else {
                    // A class that WAS filled in must say which school it is for —
                    // otherwise it would be dropped without anyone noticing.
                    out.errors.push({
                        sheet: sheetName, row: classHeader + 2, column: SCHOOL_EMAIL_LABEL,
                        message: `Required — type the Contact Email of the school the class "${info.class_name}" belongs to`,
                    });
                }
                continue;
            }
            if (!info) {
                out.errors.push({ sheet: sheetName, row: classHeader + 2, column: 'Class Name', message: 'Required' });
                continue;
            }
            if (!isValidEmail(schoolEmail)) {
                out.errors.push({ sheet: sheetName, row: classHeader + 2, column: SCHOOL_EMAIL_LABEL, message: 'Invalid email address' });
            }
            const students = parseLicenseRowsFromAoa(aoa) || [];
            const classError = (column, message) => out.errors.push({ sheet: sheetName, row: classHeader + 2, column, message });
            // The same rules Add a Class applies (and the Schema lists as required):
            // a class needs its grade; one with students also needs its teacher's email.
            if (info.class_name.length > 100) classError('Class Name', 'Too long — 100 characters at most');
            if (!info.grade_level) classError('Grade Level', 'Required');
            if (info.academic_year.length > 20) classError('Academic Year', 'Too long — 20 characters at most, for example 2026-2027');
            if (info.incharge_email && !isValidEmail(info.incharge_email)) classError('Teacher Email', 'Invalid email address');
            else if (!info.incharge_email && students.length) classError('Teacher Email', 'Required for a class with students');
            const section_code = genSectionCode();
            students.forEach((s, i) => {
                s.section_code = section_code;
                const studentError = (column, message) => out.errors.push({ sheet: sheetName, row: `student ${i + 1}`, column, message });
                [['email_address', 'Parent Email'], ['student_email', 'Learner Email']].forEach(([k, label]) => {
                    if (s[k] && !isValidEmail(s[k])) studentError(label, `"${s[k]}" is not a valid email address`);
                });
                if (!s.first_name) studentError('First Name', 'Required');
                if (!s.last_name) studentError('Last Name', 'Required');
                // A year that could not be read leaves the student without a birthdate.
                if (!s.birthdate) studentError('Year of Birth (YYYY)', 'Required — a 4-digit year, for a learner aged 8 or older');
            });
            out.classes.push({ ...info, school_email: schoolEmail, students, _sheet: sheetName, _row: classHeader + 2 });
        }
    }

    if (!foundSchoolBlock && !out.networks.length && !out.classes.length) {
        return { ...out, fatal: `This does not look like the ${isPartner ? 'schools and networks' : 'schools'} template — no "School Name" column was found. Download the template and fill that in.` };
    }

    /* ── checks across rows ── */
    const seen = new Map();
    [...out.networks, ...out.schools].forEach((r) => {
        const key = lower(r.contact_email);
        if (!key) return;
        if (seen.has(key)) {
            out.errors.push({ sheet: r._sheet, row: r._row, column: 'Contact Email', message: `Used twice in this file (also on row ${seen.get(key)}) — every school and network needs its own contact email` });
        } else seen.set(key, r._row);
    });

    const fileNetworks = new Set(out.networks.map((n) => lower(n.contact_email)).filter(Boolean));
    out.schools.forEach((s) => {
        const ne = lower(s.network_email);
        // The import template's dropdown and highlight take the Network rows of the
        // file only; the older templates also took one already in the workspace.
        if (ne && !fileNetworks.has(ne) && (importTemplate || !existingNetworks.has(ne))) {
            out.errors.push({ sheet: s._sheet, row: s._row, column: importTemplate ? 'Parent Network Email' : NETWORK_EMAIL_NAME, message: importTemplate
                ? `No Network row in this file has the Contact Email "${s.network_email}" — select a network from the list, or ${NOT_APPLICABLE}`
                : `No network with the contact email "${s.network_email}" — it is neither in this file nor already in your workspace` });
        }
    });

    const fileSchools = new Set(out.schools.map((s) => lower(s.contact_email)).filter(Boolean));
    out.classes.forEach((c) => {
        const se = lower(c.school_email);
        if (isValidEmail(c.school_email) && !fileSchools.has(se) && !existingSchools.has(se)) {
            // Name the addresses it could have been, so the mismatch is easy to spot.
            const inFile = out.schools.map((s) => s.contact_email).filter(Boolean);
            const hint = inFile.length
                ? ` It must match a school's Contact Email exactly — in this file: ${inFile.slice(0, 5).join(', ')}${inFile.length > 5 ? ', …' : ''}`
                : '';
            out.errors.push({ sheet: c._sheet, row: c._row, column: SCHOOL_EMAIL_LABEL, message: `No school with the contact email "${c.school_email}" — it is neither in this file nor already in your network.${hint}` });
        }
    });

    // A contact email already in the workspace is refused, as Add a School refuses it.
    [...out.networks, ...out.schools].filter((r) => r._exists).sort((a, b) => a._row - b._row).forEach((r) => {
        out.errors.push({ sheet: r._sheet, row: r._row, column: 'Contact Email', message: `"${r.contact_email}" already exists in your workspace — please use a different email` });
    });
    // Rows that were refused (a wrong Type) are not "no rows": their problems are listed instead.
    if (!out.networks.length && !out.schools.length && !out.classes.length && !out.errors.length) {
        return { ...out, fatal: 'The file has no rows to upload — fill in at least one school.' };
    }
    return out;
};

/* ───────────────────────── rows → API payloads ───────────────────────── */

const fullName = (r) => `${r.contact_first_name} ${r.contact_last_name}`.trim();

/**
 * One school, in the shape the Add a School form posts (CreateClientDialog's
 * handleCreate) — the school's address, phone, state and website belong to its
 * institute record. The phone number is sent as org_phone_number, the key the
 * create call stores it from.
 */
export const schoolRowToPayload = (r) => ({
    org_name: r.org_name,
    contact_first_name: r.contact_first_name,
    contact_last_name: r.contact_last_name,
    contact_title: r.contact_title,
    contact_name: fullName(r),
    contact_email: r.contact_email,
    org_address: '',
    org_city: r.city,
    postal_code: r.postal_code,
    org_country: resolveCountry(r.country),
    org_phone_number: r.phone,
    preferred_locale: '',
    callback_url: '',
    callback_key: '',
    is_school_network: false,
    number_of_learners: null,
    institute_phone_country_code: normDialCode(r.dial_code),
    institute_phone_number: r.phone,
    institute_head_title: r.contact_title,
    institute_address: r.address1,
    institute_address_line1: r.address1,
    institute_address_line2: r.address2,
    institute_state: r.state,
    institute_website: r.website,
});

/** One network (with the schools it is created with), as the Add a Network form posts it. */
export const networkRowToPayload = (r, schoolRows = []) => ({
    org_name: r.org_name,
    contact_first_name: r.contact_first_name,
    contact_last_name: r.contact_last_name,
    contact_title: r.contact_title,
    contact_name: fullName(r),
    contact_email: r.contact_email,
    org_address: r.address1,
    org_address_line2: r.address2,
    org_city: r.city,
    postal_code: r.postal_code,
    org_country: resolveCountry(r.country),
    org_state: r.state,
    org_website: r.website,
    org_phone_country_code: normDialCode(r.dial_code),
    org_phone_number: r.phone,
    preferred_locale: '',
    callback_url: '',
    callback_key: '',
    is_school_network: true,
    schools: schoolRows.map(schoolRowToPayload),
});

/** A class sheet with no students, as Add a Class creates an empty class. */
export const classToShellPayload = (c) => ({
    className: c.class_name,
    gradeLevel: c.grade_level,
    academicYear: c.academic_year,
    teacherFirstName: c.incharge_first_name,
    teacherLastName: c.incharge_last_name,
    teacherEmail: c.incharge_email,
});
